/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-position-picker.js — Position picker step (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `position_picker`. Shared bundle + multistep.
 *
 * Phase 2.5 layout, aligned 2026-06-01 to the compact-row reference
 * (docs/reference/customizer-position-method-reference.html §position_picker):
 *   - Collapsible .jrs-garment-preview (collapsed by default) — selected
 *     positions as real metaobject diagram tiles (label + image, with fallback)
 *   - .jrs-chips-list — vertical list on mobile, 2-col grid on tablet+
 *   - .jrs-chip-row per position: a single .jrs-chip-row__main line
 *     (thumb | name + optional Recommended pill | price | SQUARE multi-select
 *     check) plus a selected-only .jrs-chip-row__detail holding the
 *     description. The square check + fill (never colour alone) conveys
 *     selection; row keeps role="checkbox" + aria-checked.
 *
 * State path per Doc 6 §5.3:
 *   state.draft.positions = ['left_breast', 'right_sleeve', ...]
 *
 * Catalogue: JRS.PositionCatalogue.getAll() — provides id, name,
 * application_methods, recommended (bool), helper (string), icon
 * (inline SVG string), plus marker + view used by the garment preview.
 * Order in the catalogue determines render order (commit 18ced41 reordered
 * to match the mockup: recommended first).
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'position_picker';

  var _mountEl = null;
  var _listeners = [];
  var _garmentOpen = false; // collapsible garment preview, collapsed by default
  // The catalogue used to render the current scope: shop-level
  // PositionCatalogue.getAll() for bundle_wide/multistep, or the current
  // bundle category's positions (merged with the catalogue) for per_product.
  var _activeCatalogue = [];

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    _garmentOpen = false; // always re-enter with the preview collapsed

    JRS.CustomizerPopup.setReassurance(
      'Pick one or more. You can change this later.'
    );
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var draft = state.draft || {};
    var headerInfo = _headerInfo(state);          // per_product: category name + N-of-M
    _activeCatalogue = _resolveActiveCatalogue(state); // per-category (per_product) or shop catalogue
    // CART-EDIT ONLY: a reopened line's stored selection can contain a position the
    // garment can no longer take (now hidden by the fully-restricted / positionHandles
    // filters — e.g. a softshell's Right Sleeve). Drop those from the selection so the
    // hidden rows can't linger through to the application_method step + review + save.
    // Guarded to a non-empty catalogue so a transient empty load never wipes a
    // selection; no-op on the PDP (fresh builds only ever select visible positions).
    var selectedIds = _pruneSelectionToCatalogue(state, draft, _activeCatalogue);

    // Only one position to choose from → choose it (standing decision: the first
    // option is always selected). A bundle's FREE position (e.g. Free Left Breast)
    // is also pre-ticked, so the free logo is never skipped by accident; the
    // customer can untick it or add paid positions (Hamza 2026-10-17, order 81770).
    // Recorded exactly as a manual tap is: same draft write, same
    // pdp_customizer_decision payload, same selected-row detail block.
    var preselectId = _preselectPosition(state, context, selectedIds, _activeCatalogue);
    if (preselectId) {
      selectedIds = [preselectId];
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'positions', value: selectedIds }
      });
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
        step_id: STEP_ID,
        decision_type: 'positions',
        decision_value: selectedIds
      });
    }

    _render(_activeCatalogue, selectedIds, headerInfo);
    _bindInteractions();
    if (preselectId) {
      var rows = _mountEl.querySelectorAll('[data-jrs-action="position-toggle"]');
      for (var r = 0; r < rows.length; r++) {
        if (rows[r].getAttribute('data-jrs-value') === preselectId) _addDetail(rows[r], preselectId);
      }
    }
    _syncCta(true); // set the chrome-footer Continue CTA (count + enabled state)
  }

  // The id to preselect, or null. Counts the RESOLVED catalogue — what actually
  // renders, after buyability, headwear, hide-* / remove-* tags and bundle slot
  // rules have all run in _resolveActiveCatalogue — never the raw list. Never
  // overwrites a choice the customer already made or restored:
  //   - an existing selection (including one pruned on cart-edit)
  //   - context.isResume   — popup reopened from saved state (OPEN, resume:true)
  //   - context.direction 'back' — returned via Back from a later step
  //   - cart-edit, via editMode or the cart-edit flow key
  // Runs on mount only, so deselecting the preselected position afterwards leaves
  // it deselected; nothing re-applies it until the step is entered afresh.
  // Picks the sole position when there is one, else the first FREE position
  // (_sortFreeFirst floats free rows to the top, so only catalogue[0] can be it).
  // Never a paid position on a multi-position list.
  function _preselectPosition(state, context, selectedIds, catalogue) {
    if (!catalogue || !catalogue.length) return null;
    if (selectedIds && selectedIds.length) return null;
    if (context && context.isResume) return null;
    if (context && context.direction === 'back') return null;
    if (state && state.editMode && state.editMode.active) return null;
    if (state && state.flow === 'cart-edit') return null;
    var first = catalogue[0];
    if (!first || !first.id) return null;
    if (catalogue.length === 1) return first.id;
    return _isFreePosition(first) ? first.id : null;
  }

  // Remove selected position ids that aren't in the rendered catalogue (hidden as
  // unavailable for this garment). Cart-edit only + only when the catalogue has
  // entries. Dispatches the pruned list back to draft.positions so the app-method
  // step, review, and save all see the corrected selection. Returns the ids to
  // render as selected. Non-cart-edit / empty-catalogue → returns the selection
  // unchanged. Exposed as a test seam.
  function _pruneSelectionToCatalogue(state, draft, catalogue) {
    var current = (draft && draft.positions) || [];
    var isCartEdit = !!(state && state.flow === 'cart-edit');
    if (!isCartEdit || !catalogue || !catalogue.length || !current.length) return current;
    var valid = {};
    for (var i = 0; i < catalogue.length; i++) {
      if (catalogue[i] && catalogue[i].id) valid[catalogue[i].id] = true;
    }
    var pruned = [];
    for (var j = 0; j < current.length; j++) {
      if (Object.prototype.hasOwnProperty.call(valid, current[j])) pruned.push(current[j]);
    }
    if (pruned.length === current.length) return current; // nothing to drop
    if (window.console && typeof console.info === 'function') {
      console.info('jrs-v2-step-position-picker: cart-edit dropped ' +
        (current.length - pruned.length) + ' selected position(s) this garment can no longer take.');
    }
    if (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.dispatch === 'function') {
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'positions', value: pruned }
      });
    }
    return pruned;
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var selected = (state.draft && state.draft.positions) || [];
    if (selected.length === 0) {
      _showError('Pick at least one position.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
        step_id: STEP_ID,
        field_id: 'positions',
        reason: 'positions_required'
      });
      return { valid: false, errors: { positions: 'positions_required' } };
    }
    _clearError();
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _render(catalogue, selectedIds, headerInfo) {
    headerInfo = headerInfo || { perProduct: false };
    // per_product: Doc 7 §8.2 category heading + a "{n} of {N}" progress pill
    // above the title (reusing the existing .jrs-step__progress-pill pattern).
    var pillHtml = headerInfo.perProduct
      ? '<p class="jrs-step__progress-pill">' + headerInfo.current + ' of ' + headerInfo.total + '</p>'
      : '';
    var title = headerInfo.perProduct
      ? 'Where do you want branding on ' + _escape(headerInfo.categoryName) + '?'
      : 'Where would you like your logo?';

    // Strict backend-driven catalogue: an empty list means the position
    // metaobjects aren't populated for this product (the catalogue also logs a
    // console.error). Render a clear, diagnosable message instead of a blank picker.
    if (!catalogue || catalogue.length === 0) {
      // Instrumentation (2026-09-03): the shopper cannot proceed at all here.
      try {
        JRS.CustomizerPopup.fireAnalytics('pdp_customizer_positions_unavailable', {
          step_id: STEP_ID,
          error_reason: 'position_catalogue_empty',
          message: 'Logo positions aren’t set up for this product yet'
        });
      } catch (e) {}
      _mountEl.innerHTML =
        '<div class="jrs-step jrs-step--position-picker" data-jrs-step="position_picker">' +
        '  <header class="jrs-step__header' + (headerInfo.perProduct ? ' jrs-step__header--per-position' : '') + '">' +
             pillHtml +
        '    <h2 class="jrs-step__title">' + title + '</h2>' +
        '  </header>' +
        '  <section class="jrs-step__body">' +
        '    <p class="jrs-step__empty" data-jrs-position-empty role="alert">' +
             'Logo positions aren’t set up for this product yet — please get in touch and we’ll sort it for you.' +
        '    </p>' +
        '  </section>' +
        '</div>';
      return;
    }

    var rows = '';
    for (var i = 0; i < catalogue.length; i++) {
      rows += _renderRow(catalogue[i], selectedIds.indexOf(catalogue[i].id) !== -1);
    }

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--position-picker" data-jrs-step="position_picker">' +
      '  <header class="jrs-step__header' + (headerInfo.perProduct ? ' jrs-step__header--per-position' : '') + '">' +
           pillHtml +
      '    <h2 class="jrs-step__title">' + title + '</h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
           _renderGarmentPreview() +
      '    <div class="jrs-chips-list" role="group" aria-label="Logo positions">' +
              rows +
      '    </div>' +
      '    <p data-jrs-position-error class="jrs-step__error" hidden role="alert"></p>' +
      '  </section>' +
      '</div>';
  }

  // Secondary, collapsed-by-default position diagram preview (Phase 0.5 IA).
  // The canvas is filled lazily on first open by _renderGarmentCanvas() so the
  // initial render stays cheap; it then live-updates as positions toggle.
  function _renderGarmentPreview() {
    return (
      '<div class="jrs-garment-preview" data-jrs-garment-preview>' +
      '  <button type="button" class="jrs-garment-preview__toggle" data-jrs-action="garment-toggle" aria-expanded="false" aria-controls="jrs-garment-canvas">' +
      '    <svg class="jrs-garment-preview__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>' +
      '    <span data-jrs-garment-label>See it on the garment</span>' +
      '  </button>' +
      '  <div class="jrs-garment-preview__canvas" id="jrs-garment-canvas" data-jrs-garment-canvas aria-hidden="true"></div>' +
      '</div>'
    );
  }

  // Compact row (reference: docs/reference/customizer-position-method-reference.html
  // §"position_picker"). One line collapsed — thumb | name(+pill) | price |
  // SQUARE multi-select check. The position description lives in a
  // selected-only __detail block beneath the main line, so unselected rows
  // stay compact (~56px). The square check (not a circle) signals
  // "choose more than one"; row keeps role="checkbox" + aria-checked.
  function _renderRow(descriptor, selected) {
    var pillHtml = descriptor.recommended
      ? '<span class="jrs-chip-row__pill">Recommended</span>'
      : '';
    return (
      '<button type="button"' +
      '        class="jrs-chip-row' + (selected ? ' jrs-chip-row--selected' : '') + '"' +
      '        role="checkbox"' +
      '        aria-checked="' + (selected ? 'true' : 'false') + '"' +
      '        data-jrs-action="position-toggle"' +
      '        data-jrs-value="' + _escape(descriptor.id) + '">' +
      '  <span class="jrs-chip-row__main">' +
      '    <span class="jrs-chip-row__thumb" aria-hidden="true">' +
             _thumbHtml(descriptor) +
             pillHtml +
      '    </span>' +
      '    <span class="jrs-chip-row__content">' +
      '      <span class="jrs-chip-row__head">' +
      '        <span class="jrs-chip-row__name">' + _escape(descriptor.name) + '</span>' +
      '      </span>' +
      '    </span>' +
      '    <span class="jrs-chip-row__meta">' +
             _renderMeta(descriptor) +
      '    </span>' +
      '    <span class="jrs-chip-row__badge" aria-hidden="true">' +
      '      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M3 8 L7 12 L13 4"/></svg>' +
      '    </span>' +
      '  </span>' +
      '</button>'
    );
  }

  // Position thumbnail: the real metaobject photo (descriptor.image — the same
  // image the live theme renders) when present, otherwise the generated SVG icon
  // (then the generic silhouette). The image is merchant-controlled via the
  // position metaobject, exactly as on the live theme.
  function _thumbHtml(descriptor) {
    if (descriptor && descriptor.image) {
      return '<img class="jrs-chip-row__thumb-img" src="' + _escape(descriptor.image) +
        '" alt="" loading="lazy" decoding="async">';
    }
    return (descriptor && descriptor.icon) || _fallbackIcon();
  }

  // Generic garment silhouette for a position whose catalogue entry has no
  // icon (defensive — the bundled catalogue always supplies one). Front
  // t-shirt outline, no marker.
  function _fallbackIcon() {
    return (
      '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="M14 4 L8 10 L8 18 L14 18 L14 46 L34 46 L34 18 L40 18 L40 10 L34 4 ' +
        'L28 4 C28 7 20 7 20 4 Z" fill="#FFFFFF" stroke="#9CA3AF" stroke-width="1.5" stroke-linejoin="round"/>' +
      '</svg>'
    );
  }

  // ---------------------------------------------------------------------
  // Per-product category scoping (Phase per_product S2 — spec D5/D8)
  // ---------------------------------------------------------------------
  // For per_product, the picker is scoped to the CURRENT bundle category: its
  // positions come from window.JRS_PDP_CONTEXT.bundle_categories[cursor]
  // .positions (attached from Blob A in S1; cursor = dense index into the
  // filtered active list), merged with the shop-level catalogue for visuals
  // (icon/marker/view/helper/recommended/default_method) per D8.
  // bundle_wide/multistep keep the full shop catalogue.

  function _handleize(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  // Header copy + progress for the current scope.
  function _headerInfo(state) {
    var draft = (state && state.draft) || {};
    if (draft.branding_strategy !== 'per_product') return { perProduct: false };
    var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
      ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    var cat = active[cursor];
    return {
      perProduct: true,
      // On cart-edit the active category carries only the generic label
      // ("Product 1"); prefer the real product name the parser captured on
      // _bundle_children (matched by categoryIndex, positional fallback). Empty
      // on the forward PDP path (no _bundle_children) → falls to cat.name.
      categoryName: _headerCategoryName(draft, cat, cursor),
      current: cursor + 1,
      total: active.length || 1
    };
  }

  function _headerCategoryName(draft, cat, cursor) {
    var kids = (draft && draft._bundle_children) || [];
    var pn = '';
    if (cat && typeof cat.categoryIndex !== 'undefined' && cat.categoryIndex !== null) {
      for (var i = 0; i < kids.length; i++) {
        if (kids[i] && Number(kids[i].categoryIndex) === Number(cat.categoryIndex) && kids[i].productName) {
          pn = kids[i].productName;
          break;
        }
      }
    }
    if (!pn && kids[cursor] && kids[cursor].productName) pn = kids[cursor].productName;
    return pn || (cat && cat.name) || 'this product';
  }

  function _resolveActiveCatalogue(state) {
    var draft = (state && state.draft) || {};
    // The no-available-method position hide (below) is scoped to CART-EDIT only.
    // On the PDP the picker already hides these positions via the bundle's Blob A
    // per-category list (type_N_positions); cart-edit has no Blob A, so it needs the
    // tag-derived hide instead. Gating on the flow keeps the PDP byte-for-byte
    // unchanged — it was correct and must stay so.
    var isCartEdit = !!(state && state.flow === 'cart-edit');
    var full = (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getAll === 'function')
      ? JRS.PositionCatalogue.getAll() : [];
    // Method-restriction context (product tags + 1-based bundle slot) applied
    // to every return path so unsupported methods are hidden consistently —
    // hide-embroidery/hide-print tags affect bundle_wide + multistep too, and
    // mix-and-match slot rules affect the per_product path.
    var ctx = _restrictionContext(state);
    if (draft.branding_strategy !== 'per_product') {
      // bundle_wide + multistep. Overlay the bundle's real per-position prices
      // from Blob A (window.JRS_PDP_CONTEXT.bundle_categories) so a position the
      // merchant made free in THIS bundle (variant price £0) reads as free — the
      // shop catalogue only knows standalone (paid) prices. Then float free rows
      // to the top. Both are no-ops for multistep (no bundle_categories, and no
      // £0 positions on a standard PDP).
      //
      // Method availability:
      //   - bundle_wide "apply to all" (bundle_children present) → the UNION of
      //     what each garment supports, resolved from each garment's OWN tags
      //     (the same per-child signal cart-submit + the receipt use). So a
      //     position shows BOTH methods when the bundle mixes a print-only and an
      //     embroidery-only garment, and only collapses to one when EVERY garment
      //     is locked to that one. This replaces the legacy parent-tag mix&match
      //     slot rule for the merged view, so the customer expresses a preference
      //     and the per-garment resolution (+ the step's expectation note) applies
      //     the correct method to each garment.
      //   - multistep (no bundle_children) → the shop catalogue narrowed by the
      //     single product's own tags (rule 3 with a real/absent slot as before).
      var kids = _bundleWideChildren();
      var restricted = kids
        ? _applyBundleWideUnion(full, kids)
        : _applyRestrictions(full, ctx);
      // CART-EDIT ONLY: hide positions the garment(s) can take NO method on (all
      // methods removed by hide-* / remove-* tags) instead of re-offering both via
      // the safety fallback. The PDP is unaffected (isCartEdit false).
      if (isCartEdit) {
        restricted = kids
          ? _filterFullyRestrictedUnion(restricted, kids)   // hide only if EVERY garment restricts it
          : _filterFullyRestricted(restricted, ctx);        // multistep: the single product's tags
      }
      return _filterBuyable(_filterHeadwear(_sortFreeFirst(_sortPrimaryFirst(_sortByName(_overlayBundlePrices(restricted))))));
    }

    var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
      ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    var cat = active[cursor];
    var catPositions = (cat && cat.positions) || [];
    // Fall back to the shop catalogue when the category has no positions, OR
    // when the metaobject's per-category position references are present-but-
    // unconfigured — names null + variant ids 0 across the board, the current
    // production state (the metaobject per-category position data isn't
    // populated; see Phase 5 polish item "Per-category position data in bundle
    // metaobjects"). The Blob A merge stays the future path, gated on real
    // data; bundle_wide already uses this same shop catalogue.
    //
    // CART-EDIT: cat.positions now carries a PARTIAL Blob A — seeded from the cart
    // line's `_bundle_raw` by cart-edit-flow PURELY so cart-submit re-derives each
    // SAVED position's pricing variant (e.g. the FREE Left Breast). It holds only
    // the positions the customer already picked and is NOT method-restricted, so it
    // must NOT be treated as the garment's full catalogue — otherwise the picker
    // would show only the already-selected positions and the customer couldn't ADD
    // any. Force the positionHandles-scoped, tag-restricted fallback on cart-edit
    // (the documented "cart-edit has no Blob A" path). Only the forward PDP consumes
    // the real, complete, pre-restricted per-category Blob A (type_N_positions) below.
    if (isCartEdit || !_hasRealBlobPositionData(catPositions)) {
      if (window.console && typeof console.warn === 'function') {
        console.warn('jrs-v2-step-position-picker: per_product category ' + cursor +
          ' has no usable Blob A position data (empty, or null name + zero variant ids) — ' +
          'falling back to the shop catalogue (same positions as bundle_wide).');
      }
      // Cart-edit per_product: Blob A per-category positions aren't emitted on the
      // cart page, but the bundle metaobject's per-category position handles ARE
      // (data-jrs-bundle-category-meta → cat.positionHandles, via cart-edit-flow's
      // _reconstructBundleCategoriesForContext). Narrow the shop-catalogue fallback
      // to just this garment's own positions, so a position the garment doesn't
      // offer (e.g. a softshell gilet's Right Sleeve) is hidden — reproducing the
      // PDP. No-op (fail open) on the forward PDP (positionHandles absent) and when
      // the handle list is empty.
      var scopedFull = _filterToCategoryHandles(full, cat && cat.positionHandles);
      // CART-EDIT ONLY: hide positions this garment can take NO method on (all
      // removed by its hide-* / remove-* tags — e.g. the softshell's Right Sleeve
      // with hide-print + remove-right-sleeve-embroidery). On the PDP this branch is
      // only hit when the bundle has no Blob A data; gating on isCartEdit keeps that
      // PDP case unchanged.
      var scopedRestricted = _applyRestrictions(scopedFull, ctx);
      if (isCartEdit) scopedRestricted = _filterFullyRestricted(scopedRestricted, ctx);
      // Fallback still honours mix-and-match slot + tag rules for this slot,
      // still overlays free bundle prices (→ "Free" badge), alphabetises, pins
      // Left Breast, then floats any free position to the very top.
      return _filterBuyable(_filterHeadwear(_sortFreeFirst(_sortPrimaryFirst(_sortByName(_overlayBundlePrices(scopedRestricted))))));
    }
    // per_product already carries Blob A prices via _buildPerProductCatalogue;
    // alphabetical base, Left Breast pinned, free position floated to the top. This
    // is the PDP's real-Blob-A path (cart-edit has no Blob A → always the fallback
    // above); left unchanged so the PDP's own per-category hiding is untouched.
    return _filterBuyable(_filterHeadwear(_sortFreeFirst(_sortPrimaryFirst(_sortByName(_buildPerProductCatalogue(catPositions, full, ctx))))));
  }

  // A position is "free" when the cheapest available application method costs
  // £0 — the signal the merchant sets via a £0 pricing product (e.g. the "Free
  // Left Breast" metaobject) for a bundle. Drives the "Free" badge (the only
  // meta the picker shows now that per-position prices are hidden). The both-null
  // case (placeholder / demo catalogue with no merchant prices) is NOT treated
  // as free, so a demo PDP with no price data doesn't badge every row "Free".
  function _isFreePosition(descriptor) {
    if (!descriptor) return false;
    var emb = (typeof descriptor.embroideryPrice === 'number') ? descriptor.embroideryPrice : null;
    var prn = (typeof descriptor.printPrice === 'number') ? descriptor.printPrice : null;
    if (emb === null && prn === null) return false;
    var min = null;
    if (emb !== null) min = emb;
    if (prn !== null && (min === null || prn < min)) min = prn;
    return min === 0;
  }

  // Alphabetical by display name — the base order (large_back, large_front,
  // left_breast, left_sleeve, nape_of_neck, right_breast, right_sleeve). getAll()
  // returns positions in raw backend-tag discovery order, which isn't guaranteed
  // alphabetical; sorting here makes the picker deterministic across all flows.
  // _sortPrimaryFirst then lifts Left Breast above this base. Returns a new array.
  function _sortByName(catalogue) {
    if (!catalogue || catalogue.length < 2) return catalogue;
    return catalogue.slice().sort(function (a, b) {
      var an = String((a && (a.name || a.id)) || '').toLowerCase();
      var bn = String((b && (b.name || b.id)) || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  }

  // Left Breast is the primary logo position (over the heart — the most common
  // placement, and the free one on the free-logo bundle). Per Hamza it leads on
  // BOTH the multistep PDP and the bundle PDP so the two pickers share one order:
  // "Left Breast, Large Back, Large Front, …". Everything else keeps the
  // alphabetical order from _sortByName. Returns the input untouched when Left
  // Breast isn't present (→ pure alphabetical).
  var PRIMARY_POSITION_ID = 'left_breast';
  function _sortPrimaryFirst(catalogue) {
    if (!catalogue || catalogue.length < 2) return catalogue;
    var primary = [], rest = [];
    for (var i = 0; i < catalogue.length; i++) {
      (catalogue[i] && catalogue[i].id === PRIMARY_POSITION_ID ? primary : rest).push(catalogue[i]);
    }
    if (!primary.length) return catalogue;
    return primary.concat(rest);
  }

  // ---------------------------------------------------------------------
  // Headwear type scoping (cap vs beanie)
  // ---------------------------------------------------------------------
  // A bundle's headwear slot is one product — a CAP (snapback) OR a BEANIE. The
  // shop catalogue merges both position sets (beanie_front + cap_front), so both
  // show. We keep only the positions for the headwear the customer actually
  // chose, detected from the bundle child's product name/handle/tags (the same
  // way the live theme keys off `position.title contains 'Beanie'`).
  // CONSERVATIVE: only filter when EXACTLY ONE headwear type is present — a
  // garment-only bundle, an ambiguous case, or both types present → no change
  // (so this can never over-filter or regress a normal bundle).

  // All bundle children in scope, with their `tags` (D1). Forward PDP: the
  // trigger's JRS_PDP_CONTEXT.bundle_children; cart-edit: draft._bundle_children.
  function _allBundleChildren() {
    var fwd = _bundleWideChildren();
    if (fwd && fwd.length) return fwd;
    var st = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getState === 'function')
      ? JRS.CustomizerPopupState.getState() : {};
    return ((st && st.draft) || {})._bundle_children || [];
  }

  // 'beanie' | 'cap' | null for a position, from its id AND name.
  function _headwearTypeOfPosition(d) {
    var n = (String((d && d.id) || '') + ' ' + String((d && d.name) || '')).toLowerCase();
    if (n.indexOf('beanie') !== -1) return 'beanie';
    if (n.indexOf('cap') !== -1) return 'cap';
    return null; // non-headwear position (garment) — never filtered
  }

  // The headwear type the customer chose for this bundle, or null when it can't
  // be told unambiguously (→ no filtering). Reads each child's product
  // title/name/handle + tags for 'beanie' vs 'cap'/'snapback'.
  function _selectedHeadwearType() {
    var kids = _allBundleChildren();
    if (!kids || !kids.length) return null;
    var hasBeanie = false, hasCap = false;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i] || {};
      var tags = (k.tags && k.tags.join) ? k.tags.join(' ') : '';
      var s = (String(k.productTitle || k.productName || '') + ' ' +
               String(k.productHandle || '') + ' ' + tags).toLowerCase();
      if (s.indexOf('beanie') !== -1) hasBeanie = true;
      if (s.indexOf('snapback') !== -1 || /\bcap\b/.test(s) || s.indexOf('cap-') !== -1 || s.indexOf('cap_') !== -1) hasCap = true;
    }
    if (hasBeanie && !hasCap) return 'beanie';
    if (hasCap && !hasBeanie) return 'cap';
    return null;
  }

  // Keep only the chosen headwear type's positions (+ every non-headwear
  // position). No-op when the type is ambiguous or nothing would be dropped.
  function _filterHeadwear(catalogue) {
    if (!catalogue || catalogue.length < 2) return catalogue;
    var type = _selectedHeadwearType();
    if (!type) return catalogue;
    var out = [];
    var dropped = false;
    for (var i = 0; i < catalogue.length; i++) {
      var ht = _headwearTypeOfPosition(catalogue[i]);
      if (ht && ht !== type) { dropped = true; continue; }
      out.push(catalogue[i]);
    }
    if (!dropped) return catalogue;
    return out.length ? out : catalogue; // never strand the picker empty
  }

  // Narrow a catalogue to a whitelist of position handles — the CURRENT per_product
  // garment category's own positions (bundle metaobject type_N_positions), supplied
  // in cart-edit via cat.positionHandles. This reproduces the PDP's per-garment
  // position hiding when the picker falls back to the shop catalogue (Blob A absent
  // on the cart page). Ids and the whitelist are both handleized so Liquid's
  // `handleize` output (left-breast) reconciles with the catalogue id (left_breast).
  // Empty/absent whitelist → return the input unchanged (fail open on the PDP + when
  // no per-category data). Never strands the picker empty (a whitelist that matches
  // nothing falls back to the full list). Exposed as a test seam.
  function _filterToCategoryHandles(catalogue, handles) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    if (!handles || !handles.length) return catalogue;
    var want = {};
    for (var h = 0; h < handles.length; h++) {
      var k = _handleize(handles[h]);
      if (k) want[k] = true;
    }
    var out = [];
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      if (d && Object.prototype.hasOwnProperty.call(want, _handleize(d.id))) out.push(d);
    }
    return out.length ? out : catalogue;
  }

  // Hide a position the garment can take NO method on — every method removed by its
  // hide-* / remove-{pos}-{method} tags (e.g. a softshell tagged hide-print AND
  // remove-right-sleeve-embroidery → Right Sleeve has neither embroidery nor print).
  // JRS.MethodRestrictions.getAllowedMethods can't express this (it safety-nets to
  // both methods so the app-method step always has something to pick), so we use
  // getAllowedMethodsRaw (no fallback) here to decide VISIBILITY: an empty raw set
  // means the position must not be offered at all — the PDP behaviour the merchant's
  // tags intend. Applied per position with the current scope's tags + slot. No-op
  // when MethodRestrictions is absent or nothing is fully restricted. Exposed as a
  // test seam.
  function _filterFullyRestricted(catalogue, ctx) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    if (!ctx || !JRS.MethodRestrictions ||
        typeof JRS.MethodRestrictions.getAllowedMethodsRaw !== 'function') {
      return catalogue;
    }
    var out = [];
    var dropped = false;
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      var raw = JRS.MethodRestrictions.getAllowedMethodsRaw({
        positionId: d.id,
        productTags: ctx.productTags,
        bundleSlot: ctx.slot,
        embroideryVariantId: d.embroideryVariantId,
        printVariantId: d.printVariantId,
        baseMethods: d.application_methods
      });
      if (raw && raw.length) {
        out.push(d);
      } else {
        dropped = true;
        if (window.console && typeof console.info === 'function') {
          console.info('jrs-v2-step-position-picker: hiding position "' + d.id +
            '" — the garment allows no application method here (all removed by hide-* / remove-* tags).');
        }
      }
    }
    return dropped ? out : catalogue;
  }

  // bundle_wide analogue of _filterFullyRestricted: a position is hidden only when
  // EVERY garment in the bundle fully restricts it (raw allowed empty for all
  // children). If any garment can take a method there, the position stays and the
  // per-garment method engine resolves each garment's method. No-op without children
  // / MethodRestrictions. Exposed as a test seam.
  function _filterFullyRestrictedUnion(catalogue, children) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    if (!children || !children.length || !JRS.MethodRestrictions ||
        typeof JRS.MethodRestrictions.getAllowedMethodsRaw !== 'function') {
      return catalogue;
    }
    var out = [];
    var dropped = false;
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      var anyAllows = false;
      for (var c = 0; c < children.length; c++) {
        var raw = JRS.MethodRestrictions.getAllowedMethodsRaw({
          positionId: d.id,
          productTags: (children[c] && children[c].tags) || [],
          bundleSlot: null,
          embroideryVariantId: d.embroideryVariantId,
          printVariantId: d.printVariantId,
          baseMethods: d.application_methods
        });
        if (raw && raw.length) { anyAllows = true; break; }
      }
      if (anyAllows) out.push(d);
      else dropped = true;
    }
    return dropped ? out : catalogue;
  }

  // Drop positions that can NEVER be added to cart: a descriptor whose
  // positionVariantId resolved to 0 has no variant for EITHER method
  // (jrs-v2-position-catalogue.js _resolvePositionVariantId only returns 0 when
  // both the embroidery and print variant references are missing). If it were
  // offered, the customer could pick it and then hit a hard "Position N has no
  // positionVariantId" build error / Shopify "Cannot find variant" at Confirm.
  // Hiding it turns that hard failure into "simply not shown"; if EVERY position
  // is broken the list goes empty and the picker shows its "positions
  // unavailable" message (the honest state). Only positionVariantId === 0 (the
  // explicit sentinel) is dropped — a missing/undefined field (test stubs) is
  // kept. Exposed as a test seam.
  function _filterBuyable(catalogue) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    var out = [];
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      if (d && d.positionVariantId === 0) {
        if (window.console && typeof console.warn === 'function') {
          console.warn('jrs-v2-step-position-picker: hiding position "' + d.id +
            '" from the picker — it has no usable variant id, so add-to-cart would ' +
            'fail. Populate its embroidery/print variant references in the position metaobject.');
        }
        continue;
      }
      out.push(d);
    }
    return out;
  }

  // Float any FREE position (£0 cheapest method — the merchant's free-logo
  // position for this bundle) to the very top, above the Left Breast pin. The
  // free position is the headline offer, so it leads regardless of name or the
  // primary pin. Applied last (outermost). Stable: free rows keep their incoming
  // relative order, paid rows keep theirs. No-op when nothing is free (e.g. a
  // standalone multistep PDP, where prices are all paid). Returns a new array
  // only when it reorders. Exposed as a test seam.
  function _sortFreeFirst(catalogue) {
    if (!catalogue || catalogue.length < 2) return catalogue;
    var free = [], paid = [];
    for (var i = 0; i < catalogue.length; i++) {
      (_isFreePosition(catalogue[i]) ? free : paid).push(catalogue[i]);
    }
    if (!free.length) return catalogue; // nothing free → unchanged
    return free.concat(paid);
  }

  // Overlay the bundle's real per-position prices from Blob A
  // (window.JRS_PDP_CONTEXT.bundle_categories) onto a shop-catalogue-derived
  // list, matched by handleized position name. This is what lets the bundle_wide
  // picker see a £0 (free) position — the shop catalogue only has standalone
  // prices. Mirrors what _buildPerProductCatalogue already does for per_product.
  // Clones each touched descriptor (never mutates the shared catalogue). No-op
  // for multistep (no bundle_categories) and for positions absent from Blob A.
  function _overlayBundlePrices(catalogue) {
    var blobCats = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                    window.JRS_PDP_CONTEXT.bundle_categories) || [];
    if (!blobCats.length || !catalogue || !catalogue.length) return catalogue;

    // id → cheapest-known {embP, prnP} across every category. A bundle_wide free
    // position repeats per category at the same £0; free-wins keeps the lowest.
    var priceById = {};
    for (var c = 0; c < blobCats.length; c++) {
      var positions = (blobCats[c] && blobCats[c].positions) || [];
      for (var p = 0; p < positions.length; p++) {
        var bp = positions[p];
        if (!bp || !bp.name) continue;
        var embP = (typeof bp.embroideryPrice === 'number') ? bp.embroideryPrice : null;
        var prnP = (typeof bp.printPrice === 'number') ? bp.printPrice : null;
        if (embP === null && prnP === null) continue;
        var pid = _handleize(bp.name);
        var cur = priceById[pid];
        if (!cur) {
          priceById[pid] = { embP: embP, prnP: prnP };
        } else {
          if (embP !== null && (cur.embP === null || embP < cur.embP)) cur.embP = embP;
          if (prnP !== null && (cur.prnP === null || prnP < cur.prnP)) cur.prnP = prnP;
        }
      }
    }

    var out = [];
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      var bpp = d && priceById[d.id];
      if (!bpp) { out.push(d); continue; }
      var clone = {};
      for (var k in d) { if (Object.prototype.hasOwnProperty.call(d, k)) clone[k] = d[k]; }
      if (bpp.embP !== null) clone.embroideryPrice = bpp.embP;
      if (bpp.prnP !== null) clone.printPrice = bpp.prnP;
      out.push(clone);
    }
    return out;
  }

  // Restriction context for the current scope: the tags JRS.MethodRestrictions
  // reads to hide unsupported methods, plus the 1-based bundle slot (per_product
  // only — Blob A categoryIndex + 1).
  //
  // Tag source by flow (this is what makes hide-embroidery / hide-print behave
  // the same on EVERY option):
  //   • multistep / single product — the PDP product's own tags.
  //   • bundle_wide                 — parent tags here; the per-child union is
  //                                   applied separately (_applyBundleWideUnion).
  //   • per_product                 — the ACTIVE category's configured child tags
  //                                   (each bundle child carries its own `tags`,
  //                                   plumbed by the trigger), so a child's hide-*
  //                                   / remove-* tag restricts THAT garment —
  //                                   matching bundle_wide + multistep. The
  //                                   bundle-level mix&match markers are carried
  //                                   over from the parent so the large-placement
  //                                   slot rule still fires; the parent's hide-* /
  //                                   remove-* tags are NOT carried (they must
  //                                   come from the garment). Falls back to the
  //                                   parent tags when the child can't be resolved
  //                                   (byte-safe no-op).
  function _restrictionContext(state) {
    var draft = (state && state.draft) || {};
    var parentTags = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                      window.JRS_PDP_CONTEXT.productTags) || [];
    var tags = parentTags;
    var slot = null;
    if (draft.branding_strategy === 'per_product') {
      var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
        ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
      var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
        ? draft._current_category_index : 0;
      var cat = active[cursor];
      if (cat && typeof cat.categoryIndex === 'number') slot = cat.categoryIndex + 1;
      var childTags = _activeCategoryChildTags(cat);
      if (childTags) tags = _carryBundleMarkers(childTags, parentTags);
    }
    return { productTags: tags, slot: slot };
  }

  // The tags of the bundle child/children configured in a per_product category,
  // matched by handle (cat.productHandles ∩ bundle_children[].productHandle) and
  // unioned. Because JRS.MethodRestrictions' hide-* / remove-* rules are
  // removal-only, unioning a category's children yields the INTERSECTION of
  // allowed methods (a method is hidden if ANY configured product in the category
  // can't do it) — the correct rule for the single per-category method choice.
  // Returns null when nothing matches, so the caller keeps the parent-tag fallback.
  function _activeCategoryChildTags(cat) {
    if (!cat) return null;
    var handles = cat.productHandles || [];
    if (!handles.length) return null;
    var kids = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                window.JRS_PDP_CONTEXT.bundle_children) || [];
    if (!kids.length) return null;
    var wanted = {};
    for (var h = 0; h < handles.length; h++) wanted[handles[h]] = true;
    var out = [];
    var seen = {};
    var matched = false;
    for (var i = 0; i < kids.length; i++) {
      var kid = kids[i];
      if (!kid || !wanted[kid.productHandle]) continue;
      matched = true;
      var kt = kid.tags || [];
      for (var t = 0; t < kt.length; t++) {
        var key = String(kt[t]).toLowerCase();
        if (!seen[key]) { seen[key] = true; out.push(kt[t]); }
      }
    }
    return matched ? out : null;
  }

  // Carry the bundle-level mix&match markers (a property of the BUNDLE, not of
  // any one garment) from the parent onto the child tag set, so the large-
  // placement slot rule (isMixAndMatchBundle) still fires in per_product. Only
  // these markers cross over — never hide-* / remove-*, which must come from the
  // garment being configured.
  function _carryBundleMarkers(childTags, parentTags) {
    var out = childTags.slice();
    var have = {};
    for (var i = 0; i < out.length; i++) have[String(out[i]).toLowerCase()] = true;
    var parentHas = {};
    for (var p = 0; p < parentTags.length; p++) parentHas[String(parentTags[p]).toLowerCase()] = true;
    var markers = ['bundle', 'mix&match', 'mix & match'];
    for (var m = 0; m < markers.length; m++) {
      if (parentHas[markers[m]] && !have[markers[m]]) out.push(markers[m]);
    }
    return out;
  }

  // Apply method restrictions to a ready catalogue (the shop catalogue for
  // bundle_wide/multistep, or the per_product fallback). Returns the input
  // untouched when nothing narrows (or when JRS.MethodRestrictions is absent —
  // keeps tests + any load-order edge byte-identical); copies only the
  // descriptors a rule actually changes so shared catalogue objects are never
  // mutated.
  // Large placements — their recommended method differs from small placements.
  function _isLargePos(id) {
    var n = String(id || '').toLowerCase().replace(/-/g, '_');
    return n === 'large_back' || n === 'large_front';
  }

  // The default_method a descriptor should carry — this drives the app-method
  // step's "Recommended" badge. Rule (confirmed w/ Jim 2026-07-09): on a
  // mix&match bundle's SWEATSHIRT step (the 2nd category → slot 2), large
  // placements recommend EMBROIDERY whenever it's allowed. Otherwise keep the
  // catalogue's own default, clamped to the allowed set.
  function _recommendedDefault(positionId, allowed, ctx, fallbackDefault) {
    allowed = allowed || [];
    if (ctx && ctx.slot === 2 && _isLargePos(positionId) &&
        JRS.MethodRestrictions && typeof JRS.MethodRestrictions.isMixAndMatchBundle === 'function' &&
        JRS.MethodRestrictions.isMixAndMatchBundle(ctx.productTags) &&
        allowed.indexOf('embroidery') !== -1) {
      return 'embroidery';
    }
    if (allowed.indexOf(fallbackDefault) !== -1) return fallbackDefault;
    return allowed.length ? allowed[0] : fallbackDefault;
  }

  function _applyRestrictions(catalogue, ctx) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    if (!ctx || !JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') {
      return catalogue;
    }
    var out = catalogue.slice();
    var changedAny = false;
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      var base = d.application_methods || ['embroidery', 'print'];
      var allowed = JRS.MethodRestrictions.getAllowedMethods({
        positionId: d.id,
        productTags: ctx.productTags,
        bundleSlot: ctx.slot,
        embroideryVariantId: d.embroideryVariantId,
        printVariantId: d.printVariantId,
        baseMethods: base
      });
      var newDefault = _recommendedDefault(d.id, allowed, ctx, d.default_method);
      // Skip only when NOTHING changes: methods unchanged AND the recommended
      // default already matches (the sweatshirt-step-2 large case flips the
      // default to embroidery WITHOUT narrowing the methods).
      if (allowed.length === base.length && newDefault === d.default_method) continue;
      var copy = _shallowCopy(d);
      copy.application_methods = allowed;
      copy.default_method = newDefault;
      out[i] = copy;
      changedAny = true;
    }
    return changedAny ? out : catalogue;
  }

  // Bundle children (each with its own `tags`, plumbed by the PDP trigger's
  // _readBundleChildren) for the current bundle_wide "apply to all" view. null
  // on multistep / before the trigger populated them (→ caller keeps the
  // parent-tag restriction path). Exposed as a test seam.
  function _bundleWideChildren() {
    var kids = (typeof window !== 'undefined' && window.JRS_PDP_CONTEXT &&
                window.JRS_PDP_CONTEXT.bundle_children) || null;
    return (kids && kids.length) ? kids : null;
  }

  // bundle_wide method availability: a position offers the UNION of the methods
  // each garment in the bundle supports, each garment resolved from its OWN tags
  // (JRS.MethodRestrictions.getAllowedMethods — the same per-child signal
  // cart-submit + the success receipt use). So a position shows BOTH methods
  // when the bundle mixes a print-only and an embroidery-only garment, and
  // collapses to a single method only when EVERY garment is locked to it. The
  // customer expresses a preference; the per-garment resolution applies the
  // right method to each garment (with the step's expectation note explaining
  // it). Clones only the descriptors whose set actually narrows; never strands a
  // position with zero methods. Exposed as a test seam.
  function _applyBundleWideUnion(catalogue, children) {
    if (!catalogue || !catalogue.length) return catalogue || [];
    if (!children || !children.length ||
        !JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') {
      return catalogue;
    }
    var out = catalogue.slice();
    var changedAny = false;
    for (var i = 0; i < catalogue.length; i++) {
      var d = catalogue[i];
      var base = d.application_methods || ['embroidery', 'print'];
      var union = [];
      for (var c = 0; c < children.length; c++) {
        var allowed = JRS.MethodRestrictions.getAllowedMethods({
          positionId: d.id,
          productTags: (children[c] && children[c].tags) || [],
          bundleSlot: null,
          embroideryVariantId: d.embroideryVariantId,
          printVariantId: d.printVariantId,
          baseMethods: base
        });
        for (var a = 0; a < allowed.length; a++) {
          if (union.indexOf(allowed[a]) === -1) union.push(allowed[a]);
        }
      }
      if (!union.length || union.length >= base.length) continue; // nothing narrows
      // Keep base order (embroidery, print) for stable rendering.
      var narrowed = [];
      for (var b = 0; b < base.length; b++) {
        if (union.indexOf(base[b]) !== -1) narrowed.push(base[b]);
      }
      var copy = _shallowCopy(d);
      copy.application_methods = narrowed;
      if (narrowed.indexOf(copy.default_method) === -1) copy.default_method = narrowed[0];
      out[i] = copy;
      changedAny = true;
    }
    return changedAny ? out : catalogue;
  }

  // Narrow a single position's derived methods through JRS.MethodRestrictions
  // when present; no-op (returns the input) when absent.
  function _restrictMethods(positionId, methods, embV, prnV, ctx) {
    if (!ctx || !JRS.MethodRestrictions || typeof JRS.MethodRestrictions.getAllowedMethods !== 'function') {
      return methods;
    }
    return JRS.MethodRestrictions.getAllowedMethods({
      positionId: positionId,
      productTags: ctx.productTags,
      bundleSlot: ctx.slot,
      embroideryVariantId: embV,
      printVariantId: prnV,
      baseMethods: methods
    });
  }

  function _shallowCopy(o) {
    var c = {};
    for (var k in o) { if (Object.prototype.hasOwnProperty.call(o, k)) c[k] = o[k]; }
    return c;
  }

  // Resolve a single position's descriptor for the CURRENT scope, with method
  // restrictions already applied. Reads live popup state when none is passed
  // (the application_method step calls it after the picker has run). Returns
  // null when the id isn't in the active scope.
  function getActiveDescriptor(id, state) {
    var st = state ||
      ((JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getState === 'function')
        ? JRS.CustomizerPopupState.getState() : {});
    var catalogue = _resolveActiveCatalogue(st || {});
    for (var i = 0; i < catalogue.length; i++) {
      if (catalogue[i].id === id) return catalogue[i];
    }
    return null;
  }

  // True when the category's Blob A positions carry usable data: at least one
  // entry with a non-empty name AND a non-zero embroidery/print variant id.
  // When false (empty array, or the metaobject's per-category position
  // references are unconfigured → null name / zero ids), per_product falls back
  // to the shop catalogue. Exposed as a test seam.
  function _hasRealBlobPositionData(catPositions) {
    if (!catPositions || !catPositions.length) return false;
    for (var i = 0; i < catPositions.length; i++) {
      var p = catPositions[i] || {};
      var hasName = (p.name != null && String(p.name).length > 0);
      var hasVariant = (typeof p.embroideryVariantId === 'number' && p.embroideryVariantId > 0) ||
                       (typeof p.printVariantId === 'number' && p.printVariantId > 0);
      if (hasName && hasVariant) return true;
    }
    return false;
  }

  // Merge each category position (Blob A: name + per-method variant ids +
  // prices) with the shop catalogue descriptor (matched by handleized name)
  // for its visuals. On no match: warn + render a minimal descriptor (Blob A
  // data + fallback icon). Exposed as a test seam.
  function _buildPerProductCatalogue(catPositions, full, ctx) {
    full = full || ((JRS.PositionCatalogue && JRS.PositionCatalogue.getAll()) || []);
    var byId = {};
    for (var i = 0; i < full.length; i++) byId[full[i].id] = full[i];
    var out = [];
    for (var p = 0; p < catPositions.length; p++) {
      var bp = catPositions[p] || {};
      var id = _handleize(bp.name);
      var embV = (typeof bp.embroideryVariantId === 'number') ? bp.embroideryVariantId : 0;
      var prnV = (typeof bp.printVariantId === 'number') ? bp.printVariantId : 0;
      var base = byId[id];
      var methods = [];
      if (embV > 0) methods.push('embroidery');
      if (prnV > 0) methods.push('print');
      if (!methods.length) methods = base ? base.application_methods.slice() : ['embroidery', 'print'];
      // Integrity guard: identical embroidery + print variant ids → one SKU, so a
      // method choice can ship the wrong decoration (a real Large Front cart line
      // added the Embroidery variant for a "Print" selection). Collapse to one
      // method + log; fix the metaobject to give print a distinct variant.
      if (embV > 0 && prnV > 0 && embV === prnV) {
        methods = ['embroidery'];
        if (window.console && typeof console.error === 'function') {
          console.error('jrs-v2-step-position-picker: position "' + bp.name + '" (id "' + id +
            '") has identical embroidery + print variant ids (' + embV + ') — offering ' +
            'embroidery only. Give print its own variant in the position metaobject.');
        }
      }
      // Narrow by tag + mix-and-match slot rules (no-op when the rules module
      // is absent — keeps the seam's unit tests byte-identical).
      methods = _restrictMethods(id, methods, embV, prnV, ctx);
      var embP = (typeof bp.embroideryPrice === 'number') ? bp.embroideryPrice : (base ? base.embroideryPrice : null);
      var prnP = (typeof bp.printPrice === 'number') ? bp.printPrice : (base ? base.printPrice : null);
      // Blob A now carries the position metaobject image (bundle-new.liquid) so a
      // category position outside the page-emitted shop set (e.g. beanie_front /
      // cap_front on a bundle whose parent resolves the default garment set) still
      // renders its real photo instead of the generic t-shirt fallback.
      var bpImg = (typeof bp.image === 'string') ? bp.image : '';
      if (base) {
        out.push({
          id: base.id,
          name: base.name || bp.name,
          positionVariantId: embV || prnV || base.positionVariantId,
          application_methods: methods,
          default_method: _recommendedDefault(base.id, methods, ctx, base.default_method),
          recommended: base.recommended,
          helper: base.helper,
          image: base.image || bpImg,
          icon: base.icon,
          marker: base.marker,
          view: base.view,
          embroideryPrice: embP,
          printPrice: prnP,
          embroideryVariantId: embV,
          printVariantId: prnV
        });
      } else {
        if (window.console && typeof console.warn === 'function') {
          console.warn('jrs-v2-step-position-picker: per_product position "' + bp.name +
            '" (id "' + id + '") not in the shop catalogue — rendering without catalogue visuals.');
        }
        out.push({
          id: id,
          name: bp.name || id,
          positionVariantId: embV || prnV || 0,
          application_methods: methods,
          default_method: _clampDefaultMethod('embroidery', methods),
          recommended: false,
          helper: '',
          image: bpImg,
          icon: '',
          marker: '',
          view: 'front',
          embroideryPrice: embP,
          printPrice: prnP,
          embroideryVariantId: embV,
          printVariantId: prnV
        });
      }
    }
    return out;
  }

  // Keep a descriptor's default_method inside its (possibly restricted)
  // method set: a position locked to print only can't default to embroidery.
  function _clampDefaultMethod(preferred, methods) {
    if (methods && methods.length && methods.indexOf(preferred) === -1) return methods[0];
    return preferred;
  }

  function _descriptorById(id) {
    for (var i = 0; i < _activeCatalogue.length; i++) {
      if (_activeCatalogue[i].id === id) return _activeCatalogue[i];
    }
    return (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getById === 'function')
      ? JRS.PositionCatalogue.getById(id) : null;
  }

  // ---------------------------------------------------------------------
  // Garment preview (collapsible) + CTA count
  // ---------------------------------------------------------------------

  // Fill the preview canvas from the current selection: one tile per selected
  // position showing the real metaobject diagram (descriptor.image) + label.
  // Replaces the old hardcoded front/back SVG silhouettes so every garment
  // type (apparel, caps, beanies, trousers, aprons, bags, scarves) works.
  // Called on first open and whenever selection changes while open.
  // Selected ids come from draft.positions — same source the summary/CTA uses.
  function _renderGarmentCanvas() {
    if (!_mountEl) return;
    var canvas = _mountEl.querySelector('[data-jrs-garment-canvas]');
    if (!canvas) return;

    var state = JRS.CustomizerPopupState.getState();
    var selectedIds = ((state.draft || {}).positions) || [];
    var n = selectedIds.length;
    var caption = (n === 0)
      ? 'No positions selected yet'
      : n + (n === 1 ? ' position selected' : ' positions selected');

    // Zero selected → no empty <ul>; caption only.
    if (n === 0) {
      canvas.innerHTML =
        '<span class="jrs-garment-preview__caption">' + caption + '</span>';
      return;
    }

    var tiles = '';
    for (var i = 0; i < selectedIds.length; i++) {
      tiles += _renderPositionPreviewTile(selectedIds[i]);
    }

    canvas.innerHTML =
      '<ul class="jrs-garment-preview__positions" data-jrs-position-previews>' +
        tiles +
      '</ul>' +
      '<span class="jrs-garment-preview__caption">' + caption + '</span>';

    // Runtime 404s → swap to the same neutral placeholder media box.
    _bindPreviewImageFallbacks(canvas);
  }

  // Resolve label + image for one selected position id. Prefer the active
  // (scoped) catalogue — per_product already points at the current category's
  // positions — then fall back to a case-insensitive title match, then a
  // label-only tile when the blob has no entry / no image.
  function _resolvePreviewPosition(id) {
    var d = _descriptorById(id);
    if (d) return d;

    var needle = _normalisePreviewKey(id);
    var list = _activeCatalogue || [];
    for (var i = 0; i < list.length; i++) {
      var c = list[i];
      if (!c) continue;
      if (_normalisePreviewKey(c.id) === needle) return c;
      if (_normalisePreviewKey(c.name) === needle) return c;
    }

    // Shop-wide catalogue (unscoped) — covers a selected id that left the
    // active list after a category switch mid-session.
    if (JRS.PositionCatalogue && typeof JRS.PositionCatalogue.getAll === 'function') {
      var all = JRS.PositionCatalogue.getAll() || [];
      for (var j = 0; j < all.length; j++) {
        var a = all[j];
        if (!a) continue;
        if (_normalisePreviewKey(a.id) === needle) return a;
        if (_normalisePreviewKey(a.name) === needle) return a;
      }
    }

    // Lookup miss — still show a tile with a readable label (never blank).
    return { id: id, name: String(id || 'Position'), image: '' };
  }

  function _normalisePreviewKey(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function _renderPositionPreviewTile(id) {
    var pos = _resolvePreviewPosition(id);
    var title = (pos && pos.name) ? String(pos.name) : String(id || 'Position');
    var imageUrl = (pos && typeof pos.image === 'string') ? pos.image : '';
    // Theme blob field is `image` (from data-jrs-position-definitions). Also
    // accept `image_url` if a future emitter uses the Doc-shaped key.
    if (!imageUrl && pos && typeof pos.image_url === 'string') imageUrl = pos.image_url;

    var mediaHtml;
    if (imageUrl) {
      mediaHtml =
        '<span class="jrs-garment-preview__position-media">' +
          '<img class="jrs-garment-preview__position-image"' +
          ' src="' + _escape(imageUrl) + '"' +
          ' alt="' + _escape(title) + ' — logo position shown on garment"' +
          ' loading="lazy" decoding="async">' +
        '</span>';
    } else {
      // Neutral placeholder — same box as a loaded image, no broken <img>.
      mediaHtml =
        '<span class="jrs-garment-preview__position-media jrs-garment-preview__position-media--empty"' +
        ' aria-hidden="true"></span>';
    }

    return (
      '<li class="jrs-garment-preview__position">' +
        mediaHtml +
        '<span class="jrs-garment-preview__position-label">' + _escape(title) + '</span>' +
      '</li>'
    );
  }

  // If a diagram URL 404s at runtime, drop the <img> so the media box falls
  // back to the empty/placeholder look (gray fill + border from CSS).
  function _bindPreviewImageFallbacks(root) {
    if (!root) return;
    var imgs = root.querySelectorAll('.jrs-garment-preview__position-image');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].addEventListener('error', _onPreviewImageError);
    }
  }

  function _onPreviewImageError(e) {
    var img = e && (e.currentTarget || e.target);
    if (!img || !img.parentNode) return;
    var media = img.parentNode;
    if (media.classList) media.classList.add('jrs-garment-preview__position-media--empty');
    media.removeChild(img);
  }

  // Is the position picker's current scope a headwear product? Per_product uses
  // the active category at the cursor; the non-per_product path (single-category
  // skip / bundle_wide) treats an all-headwear active list as headwear.
  // Kept as a test seam (_activeGarmentType) — the SVG silhouette preview that
  // previously consumed this was replaced by real position-image tiles.
  function _isHeadwearName(name) {
    return !!name && String(name).toLowerCase().replace(/[^a-z]/g, '').indexOf('headwear') !== -1;
  }

  function _activeGarmentType(state) {
    var draft = (state && state.draft) || {};
    var State = JRS.CustomizerPopupState;
    var active = (State && typeof State.getActiveCategoryList === 'function')
      ? State.getActiveCategoryList() : [];
    if (draft.branding_strategy === 'per_product') {
      var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
        ? draft._current_category_index : 0;
      var cat = active[cursor];
      return _isHeadwearName(cat && cat.name) ? 'headwear' : 'garment';
    }
    // Single-category (skip) or bundle_wide: headwear only when every active
    // category is headwear (a headwear-only bundle). Empty list → garment.
    if (active.length) {
      for (var i = 0; i < active.length; i++) {
        if (!_isHeadwearName(active[i] && active[i].name)) return 'garment';
      }
      return 'headwear';
    }
    return 'garment';
  }

  // Drive the shell's chrome-footer Continue CTA: a live selected-count
  // suffix ("· N positions") + enable only when ≥1 position is chosen.
  // initial=true sets the button (with its onClick); later calls patch it in
  // place so the click handler is never re-bound or duplicated.
  function _syncCta(initial) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    var n = (((JRS.CustomizerPopupState.getState().draft || {}).positions) || []).length;
    var sublabel = n > 0 ? '· ' + n + (n === 1 ? ' position' : ' positions') : '';
    var disabled = n === 0;
    // Both labels name the next action rather than saying "Continue": the
    // enabled one points at the application-method step, the disabled one
    // says what to do instead of offering a press that only produces the
    // validation error. Same ternary shape as logo_picker's "Add a logo to
    // continue". No full stop — they're labels.
    var label = n > 0 ? 'Choose how it\'s applied' : 'Pick a position to continue';
    if (initial && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({
        label: label,
        sublabel: sublabel,
        disabled: disabled,
        onClick: _onContinue
      });
    } else if (typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ label: label, sublabel: sublabel, disabled: disabled });
    }
  }

  /**
   * 2026-05-20 Bug 2 fix — render the actual position price from the
   * merchant catalogue's per-method prices (descriptor.embroideryPrice /
   * descriptor.printPrice / descriptor.resolvedPrice, populated by
   * jrs-v2-position-catalogue.js from [data-jrs-position-definitions]).
   *
   * Display rules:
   *   - Both methods priced and equal: "+£X.XX" (one price covers both)
   *   - Both methods priced and different: "+£min" (the application_method
   *     step shows the per-method specifics; the "from" prefix was
   *     removed per merchant request)
   *   - One method priced: "+£X.XX (method)"
   *   - Both priced as 0 (or recommended with explicit free): "Free"
   *   - Catalogue has no merchant data at all: "Free" (preserve previous
   *     behaviour for the placeholder catalogue used during demos +
   *     non-customised-product PDPs that somehow render the picker)
   *   - Catalogue has merchant data but both prices missing: empty
   *     string + console.warn — DON'T default to "Free" for a position
   *     that legitimately has no merchant entry, that's misleading.
   */
  // Right-of-name meta slot. Per-position prices are hidden on EVERY PDP
  // (merchant decision) — the picker shows only whether a position is included
  // free. A free bundle position (£0, detected via the Blob A price overlay)
  // gets a prominent "Free" badge; every paid position renders nothing (on the
  // bundle AND the standalone PDP). Left Breast leads regardless (_sortPrimaryFirst).
  function _renderMeta(descriptor) {
    if (!_isFreePosition(descriptor)) return '';
    return '<span class="jrs-chip-row__free">Free</span>';
  }

  function _showError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-position-error]');
    if (err) { err.textContent = msg; err.hidden = false; }
  }

  function _clearError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-position-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    var rows = _mountEl.querySelectorAll('[data-jrs-action="position-toggle"]');
    for (var i = 0; i < rows.length; i++) {
      _bind(rows[i], 'click', _onToggle);
    }
    var garment = _mountEl.querySelector('[data-jrs-action="garment-toggle"]');
    if (garment) _bind(garment, 'click', _onGarmentToggle);
  }

  function _onToggle(e) {
    var btn = e.currentTarget;
    var id = btn.getAttribute('data-jrs-value');
    if (!id) return;

    var current = ((JRS.CustomizerPopupState.getState().draft || {}).positions || []).slice();
    var nowSelected;
    if (current.indexOf(id) !== -1) {
      current.splice(current.indexOf(id), 1);
      nowSelected = false;
    } else {
      current.push(id);
      nowSelected = true;
    }

    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'positions', value: current }
    });

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'positions',
      decision_value: current
    });

    if (nowSelected) {
      btn.classList.add('jrs-chip-row--selected');
      btn.setAttribute('aria-checked', 'true');
      _addDetail(btn, id);
    } else {
      btn.classList.remove('jrs-chip-row--selected');
      btn.setAttribute('aria-checked', 'false');
      _removeDetail(btn);
    }

    if (_garmentOpen) _renderGarmentCanvas(); // live-update tiles while open
    _syncCta(false);

    if (current.length > 0) _clearError();
  }

  // Reveal/hide the selected-only description without re-rendering the row
  // (keeps focus on the toggled control). Helper text is read fresh from the
  // catalogue and injected via textContent so it can never be an HTML hole.
  function _addDetail(btn, id) {
    if (!btn || btn.querySelector('.jrs-chip-row__detail')) return;
    var desc = '';
    var d = _descriptorById(id);
    if (d) desc = d.helper || '';
    if (!desc) return;
    var detail = document.createElement('span');
    detail.className = 'jrs-chip-row__detail';
    var helper = document.createElement('span');
    helper.className = 'jrs-chip-row__helper';
    helper.textContent = desc;
    detail.appendChild(helper);
    btn.appendChild(detail);
  }

  function _removeDetail(btn) {
    if (!btn) return;
    var d = btn.querySelector('.jrs-chip-row__detail');
    if (d && d.parentNode) d.parentNode.removeChild(d);
  }

  function _onGarmentToggle(e) {
    var btn = e.currentTarget;
    var preview = btn.closest('[data-jrs-garment-preview]');
    var canvas = _mountEl.querySelector('[data-jrs-garment-canvas]');
    var label = _mountEl.querySelector('[data-jrs-garment-label]');

    _garmentOpen = !_garmentOpen;
    // Ensure the canvas holds current content before the reveal animates.
    if (_garmentOpen) _renderGarmentCanvas();

    btn.setAttribute('aria-expanded', _garmentOpen ? 'true' : 'false');
    // `.is-open` drives the CSS max-height/opacity transition (no `hidden`,
    // which can't animate). aria-hidden tracks state for assistive tech.
    if (preview) preview.classList.toggle('is-open', _garmentOpen);
    if (canvas) canvas.setAttribute('aria-hidden', _garmentOpen ? 'false' : 'true');
    if (label) {
      label.textContent = _garmentOpen ? 'Hide preview' : 'See it on the garment';
    }
  }

  function _onContinue() {
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;
    JRS.CustomizerPopup.goToStep('application_method', { trigger: 'primary_cta' });
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.PositionPicker = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    // Context-aware descriptor lookup for the current scope (restricted
    // methods included). The application_method step reads through this so it
    // shows the SAME allowed methods the picker rendered.
    getActiveDescriptor: getActiveDescriptor,
    // Test seams (Phase per_product S2)
    _buildPerProductCatalogue: _buildPerProductCatalogue,
    _handleize: _handleize,
    // Phase per_product S4.5 — Blob A real-data gate (null/zero → shop fallback).
    _hasRealBlobPositionData: _hasRealBlobPositionData,
    _resolveActiveCatalogue: _resolveActiveCatalogue,
    // bundle_wide per-garment method union (both when garments differ; one only
    // when every garment is locked to it) — replaces the parent-tag slot rule.
    _applyBundleWideUnion: _applyBundleWideUnion,
    _bundleWideChildren: _bundleWideChildren,
    // Free-bundle-position surfacing (2026-07-04): detect £0 bundle positions,
    // float them to the top, label them "Free".
    _isFreePosition: _isFreePosition,
    _sortByName: _sortByName,
    _sortPrimaryFirst: _sortPrimaryFirst,
    _sortFreeFirst: _sortFreeFirst,
    // Hide positions with no usable variant id (positionVariantId === 0) so an
    // unbuyable position is never offered (fixes B1 — the "Cannot find variant" /
    // "has no positionVariantId" hard error at Confirm).
    _filterBuyable: _filterBuyable,
    // Per_product cart-edit: narrow the fallback list to the garment category's own
    // positions (bundle metaobject type_N_positions → cat.positionHandles), so a
    // position the garment doesn't offer is hidden, matching the PDP.
    _filterToCategoryHandles: _filterToCategoryHandles,
    // Hide a position the garment can take NO method on (all removed by hide-* /
    // remove-* tags) instead of re-offering both via the safety fallback.
    _filterFullyRestricted: _filterFullyRestricted,
    _filterFullyRestrictedUnion: _filterFullyRestrictedUnion,
    // Cart-edit: drop a reopened line's now-unavailable selected positions so hidden
    // rows don't linger into the app-method step / review / save.
    _pruneSelectionToCatalogue: _pruneSelectionToCatalogue,
    // Default tick on entry: the sole position, or a bundle's free position.
    _preselectPosition: _preselectPosition,
    // Real metaobject photo when present (descriptor.image), else the SVG icon.
    _thumbHtml: _thumbHtml,
    // Headwear type scoping — show only the chosen cap/beanie's positions.
    _filterHeadwear: _filterHeadwear,
    _selectedHeadwearType: _selectedHeadwearType,
    _headwearTypeOfPosition: _headwearTypeOfPosition,
    _overlayBundlePrices: _overlayBundlePrices,
    _renderMeta: _renderMeta,
    // Headwear preview silhouette selector (test seam).
    _activeGarmentType: _activeGarmentType
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
